import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '../components/Footer';
import AdBanner from '../components/AdBanner';
import { SmartbouffeLogoLarge } from '../components/Logo';
import { 
  VideoIcon, DiamondIcon, BreakfastIcon, PersonIcon, CalendarIcon, SettingsIcon, LightbulbIcon,
  CheckIcon, WarningIcon, CloseIcon, TargetIcon, SearchIcon, PhoneIcon, GiftIcon, RefreshIcon,
  SmileIcon, ClockIcon, VegetarianIcon, VeganIcon, EggIcon, FishIcon, CarnivoreIcon, MealIcon,
  FleurDeLysIcon, RulerIcon, FolderIcon, AlertIcon, LockIcon, HourglassIcon, KeyIcon, DiceIcon, EditIcon, QuestionIcon
} from '../components/Icons';

// Composant pour afficher une icône inline dans le texte
const InlineIcon = ({ icon: Icon, size = 16, color, style = {} }) => (
  <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '4px', ...style }}>
    <Icon size={size} color={color} />
  </span>
);

export default function FAQ() {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (id) => {
    setOpenSection(openSection === id ? null : id);
  };

  const faqSections = [
    {
      id: 'videos',
      icon: <VideoIcon size={20} />,
      title: 'Vidéos et Extraction',
      questions: [
        {
          q: 'Quelles plateformes vidéo sont supportées ?',
          a: (
            <div>
              <p>SmartBouffe fonctionne avec :</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Fonctionne très bien :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>TikTok (tous les formats)</li>
                <li>YouTube Shorts</li>
                <li>Instagram Reels (peut être capricieux)</li>
                <li>Facebook vidéos</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Limitations :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>Instagram : Peut ne pas fonctionner avec certains comptes privés ou certains types de Reels</li>
                <li>YouTube : UNIQUEMENT les Shorts (vidéos courtes verticales). Les vidéos YouTube classiques longues ne sont PAS supportées.</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Astuce :</strong> Si une vidéo Instagram ne fonctionne pas, essayez de chercher la même recette sur TikTok !</p>
            </div>
          )
        },
        {
          q: '🍹 SmartBouffe fonctionne-t-il pour les cocktails et boissons ?',
          a: (
            <div>
              <p><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Absolument !</strong> SmartBouffe extrait aussi bien les recettes de :</p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>🍸 Cocktails classiques</strong> : Mojito, Margarita, Cosmopolitan, Old Fashioned...</li>
                <li><strong>🍷 Sangrias</strong> : Rouge, blanche, rosée, avec ou sans alcool</li>
                <li><strong>🥤 Mocktails</strong> : Virgin Mojito, Shirley Temple, boissons sans alcool</li>
                <li><strong>🧃 Smoothies & Jus</strong> : Smoothies protéinés, jus verts, détox...</li>
                <li><strong>☕ Cafés & Thés spéciaux</strong> : Latte maison, chai, matcha...</li>
                <li><strong>🍹 Drinks tendances</strong> : Spritz, Aperol, drinks TikTok viraux...</li>
              </ul>
              <p style={{ marginTop: '1rem', padding: '0.75rem', background: '#E8F5E9', borderRadius: '8px', border: '1px solid #C8E6C9' }}>
                <InlineIcon icon={LightbulbIcon} color="#2E7D32" /> <strong>Conseil :</strong> Créez une catégorie "Cocktails" ou "Bar" dans votre tableau de bord pour organiser vos recettes de boissons !
              </p>
              <p style={{ marginTop: '1rem' }}>L'IA convertit automatiquement les mesures en oz, ml ou tasses selon vos préférences québécoises.</p>
            </div>
          )
        },
        {
          q: 'Quels types de vidéos donnent les meilleurs résultats ?',
          a: (
            <div>
              <p><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Vidéos idéales (recettes ET cocktails) :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Avec narration vocale</strong> : Le créateur explique les ingrédients et les étapes à voix haute</li>
                <li><strong>Avec texte à l'écran</strong> : Les ingrédients et quantités sont affichés en texte visible</li>
                <li><strong>Avec recette dans la description</strong> : Beaucoup de créateurs mettent la recette complète dans la description de la vidéo - SmartBouffe l'extrait automatiquement !</li>
                <li><strong>Instructions claires</strong> : Les étapes de préparation sont bien détaillées</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Vidéos qui donnent des résultats limités :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Vidéos sans paroles avec seulement de la musique</strong> : L'IA ne peut pas "deviner" les ingrédients juste en regardant les images</li>
                <li><strong>Vidéos sans texte affiché ni description</strong> : Sans information écrite, l'extraction sera approximative</li>
                <li><strong>Vidéos très rapides type "ASMR cooking"</strong> : Difficile d'identifier tous les ingrédients</li>
              </ul>
              <p style={{ marginTop: '1rem', padding: '0.75rem', background: '#FFF3E0', borderRadius: '8px', border: '1px solid #FFE0B2' }}>
                <InlineIcon icon={AlertIcon} color="#E65100" /> <strong>Note importante :</strong> SmartBouffe ne peut PAS lire les commentaires des vidéos. Si la recette est uniquement dans les commentaires (et pas dans la description), l'extraction sera incomplète. Cherchez plutôt une vidéo où la recette est dans la description ou dictée à voix haute.
              </p>
              <p style={{ marginTop: '1rem', padding: '0.75rem', background: '#E8F5E9', borderRadius: '8px', border: '1px solid #C8E6C9' }}>
                <InlineIcon icon={LightbulbIcon} color="#2E7D32" /> <strong>Conseil :</strong> Avant d'extraire, vérifiez si la description de la vidéo contient la recette - c'est souvent le cas sur TikTok et Instagram !
              </p>
            </div>
          )
        },
        {
          q: 'Quelle est la durée maximale des vidéos ?',
          a: (
            <div>
              <p><InlineIcon icon={RulerIcon} /> <strong>Limites de durée :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>0 à 3:30 minutes</strong> : 1 vidéo recette <InlineIcon icon={CheckIcon} color="#4CAF50" /> (recommandé)</li>
                <li><strong>3:30 à 5:00 minutes</strong> : 2 vidéos recettes <InlineIcon icon={WarningIcon} color="#FFA726" /> (confirmation requise)</li>
                <li><strong>Plus de 5:00 minutes</strong> : <InlineIcon icon={CloseIcon} color="#DC3545" /> Bloqué</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Pourquoi ces limites ?</strong></p>
              <p>Les vidéos courtes contiennent généralement toute l'information nécessaire sans le "blabla" inutile. C'est aussi plus économique pour vous !</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={TargetIcon} color="#DC3545" /> <strong>Conseil :</strong> La plupart des recettes TikTok et Instagram Reels durent 15-90 secondes, ce qui est parfait !</p>
            </div>
          )
        },
        {
          q: 'Puis-je utiliser des photos ou captures d\'écran de recettes ?',
          a: (
            <div>
              <p><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Oui, absolument !</strong> SmartBouffe accepte :</p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>📷 Photos de recettes</strong> : Livres de cuisine, magazines, fiches recettes...</li>
                <li><strong>📱 Captures d'écran</strong> : Recettes trouvées sur le web, réseaux sociaux, blogs...</li>
                <li><strong>📄 Images de recettes</strong> : Tout format d'image avec du texte lisible</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Conseils pour de meilleurs résultats :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>Assurez-vous que le texte est bien lisible</li>
                <li>Recette sur 2 pages ? Ajoutez les 2 images !</li>
                <li>Évitez les photos floues ou mal éclairées</li>
              </ul>
              <p style={{ marginTop: '1rem', padding: '0.75rem', background: '#E8F5E9', borderRadius: '8px', border: '1px solid #C8E6C9' }}>
                <InlineIcon icon={CheckIcon} color="#2E7D32" /> <strong>Coût :</strong> 1 vidéo recette par extraction de photo ou capture d'écran
              </p>
            </div>
          )
        },
        {
          q: 'Pourquoi certaines vidéos ne fonctionnent pas ?',
          a: (
            <div>
              <p><InlineIcon icon={SearchIcon} /> <strong>Raisons possibles :</strong></p>
              <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Compte privé</strong> : Les vidéos de comptes privés Instagram/TikTok ne sont pas accessibles</li>
                <li><strong>Région bloquée</strong> : Certaines vidéos sont géo-restreintes</li>
                <li><strong>Vidéo supprimée</strong> : Le lien ne fonctionne plus car la vidéo a été retirée</li>
                <li><strong>Format incompatible</strong> : Vidéos YouTube classiques (seulement les Shorts fonctionnent)</li>
                <li><strong>Protection</strong> : Certains créateurs bloquent l'extraction</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Solution :</strong> Essayez une autre vidéo de la même recette ou cherchez sur une autre plateforme !</p>
            </div>
          )
        },
        {
          q: "Comment obtenir le lien d'une vidéo ?",
          a: (
            <div>
              <p><InlineIcon icon={PhoneIcon} /> <strong>Sur mobile :</strong></p>
              <p style={{ marginTop: '1rem' }}><strong>TikTok :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Appuyez sur le bouton "Partager" (flèche)</li>
                <li>Choisissez "Copier le lien"</li>
                <li>Collez dans SmartBouffe</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><strong>Instagram Reels :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Appuyez sur les 3 points (...)</li>
                <li>Choisissez "Copier le lien"</li>
                <li>Collez dans SmartBouffe</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><strong>YouTube Shorts :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Appuyez sur "Partager"</li>
                <li>Choisissez "Copier le lien"</li>
                <li>Collez dans SmartBouffe</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Important :</strong> Assurez-vous de copier le lien de la vidéo, pas le lien du profil !</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'abonnements',
      icon: <DiamondIcon size={20} />,
      title: 'Abonnements et Téléchargements',
      questions: [
        {
          q: 'Comment fonctionne le système d\'abonnement ?',
          a: (
            <div>
              <p><InlineIcon icon={DiamondIcon} /> <strong>Forfaits mensuels :</strong></p>
              <p style={{ marginTop: '1rem' }}>SmartBouffe propose des abonnements mensuels avec un nombre maximum de vidéos recettes par mois :</p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Gratuit</strong> : Maximum 5 vidéos recettes/mois (0 $) <InlineIcon icon={GiftIcon} color="#D4A574" /> <em>- cumulables</em></li>
                <li><strong>Starter</strong> : Maximum 12 vidéos recettes/mois (5,74 $/mois taxes incluses)</li>
                <li><strong>Standard</strong> : Maximum 25 vidéos recettes/mois (8,04 $/mois taxes incluses) ⭐ Populaire</li>
                <li><strong>Pro</strong> : Maximum 50 vidéos recettes/mois (11,49 $/mois taxes incluses)</li>
                <li><strong>Illimité</strong> : Vidéos recettes illimitées (17,23 $/mois taxes incluses)</li>
              </ul>
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                * Tous les prix incluent les taxes applicables au Québec (TPS + TVQ = 14,975%).
              </p>
              <p style={{ marginTop: '1rem', padding: '0.75rem', background: '#FFF3E0', borderRadius: '8px', border: '1px solid #FFE0B2' }}>
                <InlineIcon icon={WarningIcon} color="#E65100" /> <strong>Important :</strong> Les vidéos recettes des forfaits payants (Starter, Standard, Pro) ne sont <strong>PAS cumulables</strong> d'un mois à l'autre. Si vous n'utilisez pas toutes vos vidéos recettes dans le mois, elles sont perdues au renouvellement.
              </p>
              <p style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#E8F5E9', borderRadius: '8px', border: '1px solid #C8E6C9' }}>
                <InlineIcon icon={GiftIcon} color="#2E7D32" /> <strong>Exception :</strong> Seul le forfait <strong>Gratuit</strong> permet de cumuler les vidéos recettes non utilisées d'un mois à l'autre (ex: si vous n'extrayez rien pendant 2 mois, vous aurez 15 vidéos recettes disponibles).
              </p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={RefreshIcon} /> Vos vidéos recettes se réinitialisent automatiquement chaque mois à la date anniversaire de votre abonnement.</p>
            </div>
          )
        },
        {
          q: 'Combien coûte une extraction de recette ?',
          a: (
            <div>
              <p><InlineIcon icon={ClockIcon} /> <strong>Coût par type :</strong></p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>Vidéo courte (0-3:30 min) = 1 vidéo recette</li>
                <li>Vidéo longue (3:30-5:00 min) = 2 vidéos recettes</li>
                <li>Photo ou capture d'écran de recette = 1 vidéo recette</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Conseil :</strong> Privilégiez les vidéos courtes pour économiser vos vidéos recettes !</p>
            </div>
          )
        },
        {
          q: "Que se passe-t-il si je n'ai plus de vidéos recettes ?",
          a: (
            <div>
              <p><InlineIcon icon={SmileIcon} color="#4CAF50" /> <strong>Plusieurs options :</strong></p>
              <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Attendez le renouvellement</strong> : Vos vidéos recettes se réinitialisent chaque mois (à la date anniversaire)</li>
                <li><strong>Achetez des vidéos recettes bonus</strong> : 5 pour 3,44 $, 15 pour 8,04 $, ou 30 pour 13,79 $ taxes incluses (celles-ci n'expirent pas!)</li>
                <li><strong>Passez au forfait supérieur</strong> : Plus de vidéos recettes mensuelles</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Vos recettes restent</strong> : Toutes vos recettes déjà extraites sont sauvegardées à vie !</p>
              <p style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#E3F2FD', borderRadius: '8px', border: '1px solid #BBDEFB' }}>
                <InlineIcon icon={LightbulbIcon} color="#1976D2" /> <strong>Astuce :</strong> Les vidéos recettes bonus achetées séparément ne sont pas soumises à l'expiration mensuelle et restent disponibles jusqu'à utilisation.
              </p>
            </div>
          )
        },
        {
          q: 'Comment changer de forfait ?',
          a: (
            <div>
              <p><InlineIcon icon={SettingsIcon} /> <strong>Changement de forfait :</strong></p>
              <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>Cliquez sur le badge de vidéos recettes en haut à droite</li>
                <li>Choisissez votre nouveau forfait</li>
                <li>Le changement prendra effet au début de votre prochain cycle de facturation</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Note :</strong> Vous devez demander un changement de forfait au moins 48h avant votre date de renouvellement.</p>
            </div>
          )
        },
        {
          q: 'Comment annuler mon abonnement ?',
          a: (
            <div>
              <p><InlineIcon icon={SettingsIcon} /> <strong>Annulation :</strong></p>
              <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>Cliquez sur le badge de vidéos recettes en haut à droite</li>
                <li>Cliquez sur "Annuler mon abonnement" en bas du dialogue</li>
                <li>Vous garderez l'accès jusqu'à la fin de votre cycle actuel</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Vos données sont protégées :</strong> Même après annulation, toutes vos recettes restent accessibles avec le forfait gratuit (5 vidéos recettes/mois).</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'recipes',
      icon: <BreakfastIcon size={20} />,
      title: 'Recettes et Catégories',
      questions: [
        {
          q: 'Comment sont détectés les types de régimes ?',
          a: (
            <div>
              <p><InlineIcon icon={VegetarianIcon} color="#4CAF50" /> <strong>Détection automatique par IA :</strong></p>
              <p style={{ marginTop: '0.5rem' }}>L'intelligence artificielle analyse TOUS les ingrédients et détermine :</p>
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Végétalien</strong> <InlineIcon icon={VeganIcon} color="#4CAF50" /> : AUCUN produit animal (pas de viande, poisson, œufs, lait, miel, fromage)</li>
                <li><strong>Végétarien</strong> <InlineIcon icon={EggIcon} color="#FFA726" /> : Pas de viande ni poisson, MAIS peut contenir œufs, lait, fromage</li>
                <li><strong>Pescatarien</strong> <InlineIcon icon={FishIcon} /> : Contient du poisson/fruits de mer, mais PAS de viande</li>
                <li><strong>Carnivore</strong> <InlineIcon icon={CarnivoreIcon} color="#DC3545" /> : Contient de la viande (poulet, bœuf, porc, agneau, etc.)</li>
                <li><strong>Mixte</strong> <InlineIcon icon={MealIcon} color="#D4A574" /> : Recette mixte ou ne correspond pas aux catégories ci-dessus</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Important :</strong> Cette détection est automatique. Vérifiez toujours les ingrédients si vous avez des restrictions strictes !</p>
            </div>
          )
        },
        {
          q: 'Pourquoi certains ingrédients sont manquants ?',
          a: (
            <div>
              <p><InlineIcon icon={SearchIcon} /> <strong>L'IA fait de son mieux, mais :</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Raisons possibles :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li><strong>Ingrédient non mentionné</strong> dans la vidéo ou trop vite</li>
                <li><strong>Audio peu clair</strong> : Musique forte, accent difficile</li>
                <li><strong>Ingrédient visuel</strong> : Montré mais pas dit à voix haute</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Solution :</strong></p>
              <p>Vous pouvez modifier la recette après extraction ! Cliquez sur les 3 points → Modifier, puis ajoutez les ingrédients manquants.</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={TargetIcon} color="#DC3545" /> <strong>Astuce :</strong> Choisissez des vidéos où le créateur énonce clairement chaque ingrédient !</p>
            </div>
          )
        },
        {
          q: 'Pourquoi les unités sont en tasses et cuillères ?',
          a: (
            <div>
              <p><InlineIcon icon={FleurDeLysIcon} /> <strong>Unités québécoises :</strong></p>
              <p style={{ marginTop: '0.5rem' }}>SmartBouffe utilise les unités familières au Québec :</p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Tasses</li>
                <li>Cuillères à table (c. à table)</li>
                <li>Cuillères à thé (c. à thé)</li>
                <li>Livres (lb)</li>
                <li>Onces (oz)</li>
              </ul>
              <p style={{ marginTop: '0.5rem' }}>Pas de grammes ou millilitres ! C'est plus pratique pour cuisiner avec les ustensiles que vous avez déjà à la maison.</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={RulerIcon} /> <strong>Conversions approximatives (si besoin) :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>1 tasse = 250 ml (liquides) ou 150-250g (solides selon densité)</li>
                <li>1 c. à table = 15 ml</li>
                <li>1 c. à thé = 5 ml</li>
              </ul>
            </div>
          )
        },
        {
          q: 'Comment organiser mes recettes ?',
          a: (
            <div>
              <p><InlineIcon icon={FolderIcon} color="#D4A574" /> <strong>Système de catégories :</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Catégories par défaut :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Toutes les recettes (voir tout)</li>
                <li>Mes favoris</li>
                <li>À essayer</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><strong>Créez vos propres catégories :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Cliquez sur "+" en haut</li>
                <li>Nommez votre catégorie (ex: "Desserts", "Soupers rapides", "Meal prep")</li>
                <li>Ajoutez une image représentative</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><strong>Déplacer des recettes :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Appuyez longuement sur une recette</li>
                <li>Choisissez "Déplacer"</li>
                <li>Sélectionnez la catégorie de destination</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Astuce :</strong> Créez des catégories par type de repas ou par occasion (ex: "Réceptions", "Lunchs") !</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'profile',
      icon: <PersonIcon size={20} />,
      title: 'Profil et Préférences',
      questions: [
        {
          q: 'À quoi servent les préférences alimentaires ?',
          a: (
            <div>
              <p><InlineIcon icon={VegetarianIcon} color="#4CAF50" /> <strong>Filtrage intelligent :</strong></p>
              <p style={{ marginTop: '0.5rem' }}>Quand vous indiquez vos préférences (végétarien, végétalien, etc.), SmartBouffe :</p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li><strong>Analyse chaque recette</strong> extraite</li>
                <li><strong>Détecte les incompatibilités</strong> avec votre régime</li>
                <li><strong>Vous avertit AVANT</strong> d'ajouter la recette</li>
                <li><strong>Suggère des alternatives</strong> pour remplacer les ingrédients problématiques</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Exemple :</strong></p>
              <p>Vous êtes végétarien, vous extrayez une recette avec poulet : → SmartBouffe vous propose de remplacer le poulet par du tofu ou des pois chiches !</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Ce n'est PAS un filtre strict :</strong> Vous pouvez quand même sauvegarder la recette si vous voulez.</p>
            </div>
          )
        },
        {
          q: 'Comment fonctionnent les allergies ?',
          a: (
            <div>
              <p><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Détection d'allergènes :</strong></p>
              <p style={{ marginTop: '0.5rem' }}>Indiquez vos allergies dans votre profil. Pour chaque recette extraite, SmartBouffe :</p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li><strong>Scanne tous les ingrédients</strong></li>
                <li><strong>Détecte les allergènes</strong> (arachides, noix, fruits de mer, œufs, lait, blé, soja, sésame, sulfites)</li>
                <li><strong>Vous AVERTIT clairement</strong> si un allergène est présent</li>
                <li><strong>Suggère des substitutions</strong> quand c'est possible</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={AlertIcon} color="#DC3545" /> <strong>IMPORTANT :</strong></p>
              <p>Cette détection est automatique par IA. Si vous avez des allergies GRAVES, vérifiez TOUJOURS manuellement les ingrédients ! SmartBouffe est une aide, pas une garantie médicale.</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Mieux vaut prévenir :</strong> Ajoutez TOUTES vos allergies dans votre profil, même les mineures.</p>
            </div>
          )
        },
        {
          q: 'Puis-je modifier mes préférences ?',
          a: (
            <div>
              <p><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Oui, à tout moment !</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Comment modifier :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Allez dans "Profil" (menu en haut à droite)</li>
                <li>Cochez/décochez vos préférences alimentaires</li>
                <li>Cochez/décochez vos allergies</li>
                <li>Cliquez sur "Sauvegarder"</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><strong>Effet immédiat :</strong></p>
              <p>Les NOUVELLES recettes extraites seront analysées selon vos nouvelles préférences.</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Note :</strong> Les recettes déjà sauvegardées ne changent pas automatiquement. Vous devrez les vérifier manuellement si vos préférences changent.</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'menu',
      icon: <CalendarIcon size={20} />,
      title: 'Menu Hebdomadaire',
      questions: [
        {
          q: 'Comment utiliser le menu hebdomadaire ?',
          a: (
            <div>
              <p><InlineIcon icon={CalendarIcon} /> <strong>Planificateur de repas :</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Ajouter un repas :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Allez dans "Menu" (depuis le menu principal)</li>
                <li>Choisissez un jour et un type de repas (déjeuner, dîner, souper)</li>
                <li>Cliquez sur "+" pour ajouter une recette</li>
                <li>Sélectionnez parmi vos recettes sauvegardées</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><strong>Fonctionnalités :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Vue hebdomadaire complète</li>
                <li>Filtrer par statut (à faire, fait, à refaire)</li>
                <li>Navigation entre les semaines</li>
                <li>Retour rapide à cette semaine</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Astuce :</strong> Planifiez vos repas le dimanche pour toute la semaine. Vous gagnerez du temps et éviterez le "qu'est-ce qu'on mange ce soir ?" !</p>
            </div>
          )
        },
        {
          q: 'Qu\'est-ce que "Qu\'est-ce qu\'on mange ?" et "Inspire-moi" ?',
          a: (
            <div>
              <p><InlineIcon icon={DiceIcon} /> <strong>Fonctionnalités aléatoires :</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>"Qu'est-ce qu'on mange ?" :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Suggère UNE recette aléatoire parmi TOUTES vos recettes</li>
                <li>Parfait quand vous n'avez aucune idée !</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><strong>"Inspire-moi" :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Vous demande le statut souhaité (à faire, fait, à refaire, ou tout)</li>
                <li>Suggère une recette selon ce critère</li>
                <li>Utile pour trouver quelque chose de nouveau à essayer</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Astuce :</strong> Ces fonctions sont géniales quand vous avez beaucoup de recettes et que vous voulez redécouvrir des anciennes !</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'technical',
      icon: <SettingsIcon size={20} />,
      title: 'Technique et Sécurité',
      questions: [
        {
          q: 'Mes recettes sont-elles sauvegardées ?',
          a: (
            <div>
              <p><InlineIcon icon={CheckIcon} color="#4CAF50" /> <strong>Oui, à vie !</strong></p>
              <p style={{ marginTop: '0.5rem' }}>Toutes vos recettes extraites sont sauvegardées dans votre compte :</p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Stockage illimité</li>
                <li>Accessible de n'importe quel appareil (avec votre connexion)</li>
                <li>Ne sont JAMAIS supprimées automatiquement</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LockIcon} /> <strong>Sécurité :</strong></p>
              <p>Vos recettes sont privées et liées à votre compte. Personne d'autre ne peut les voir.</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Important :</strong> Si vous supprimez votre compte, toutes vos recettes seront définitivement supprimées !</p>
            </div>
          )
        },
        {
          q: 'Est-ce que SmartBouffe conserve les vidéos originales ?',
          a: (
            <div>
              <p><InlineIcon icon={CloseIcon} color="#DC3545" /> <strong>NON ! Jamais.</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Processus d'extraction :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Vidéo téléchargée temporairement</li>
                <li>Audio extrait pour transcription</li>
                <li>Photo du plat extraite</li>
                <li><strong>Vidéo SUPPRIMÉE immédiatement</strong></li>
                <li>Seul le texte de la recette est conservé + la photo du plat</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LockIcon} /> <strong>Respect du droit d'auteur :</strong></p>
              <p>Nous ne stockons JAMAIS les vidéos originales. Seul le contenu textuel de la recette (ingrédients, instructions) est sauvegardé, ce qui est permis.</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Vous êtes responsable :</strong> Assurez-vous d'avoir le droit d'utiliser les recettes que vous extrayez pour un usage personnel.</p>
            </div>
          )
        },
        {
          q: "Pourquoi l'extraction prend-elle du temps ?",
          a: (
            <div>
              <p><InlineIcon icon={HourglassIcon} color="#D4A574" /> <strong>Processus en plusieurs étapes :</strong></p>
              <p style={{ marginTop: '0.5rem' }}>Une extraction complète nécessite :</p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li><strong>Téléchargement</strong> (5-15 sec) : Récupère la vidéo</li>
                <li><strong>Modération</strong> (5-10 sec) : Vérifie que c'est bien une recette</li>
                <li><strong>Transcription Whisper</strong> (10-30 sec) : Convertit l'audio en texte</li>
                <li><strong>Extraction photo</strong> (5-10 sec) : Trouve la meilleure image du plat</li>
                <li><strong>Génération IA</strong> (10-30 sec) : GPT-4o analyse et structure la recette</li>
                <li><strong>Vérification</strong> (2-5 sec) : Analyse compatibilité avec votre profil</li>
              </ol>
              <p style={{ marginTop: '0.5rem' }}><strong>Total : 30-90 secondes</strong> selon la longueur de la vidéo</p>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>C'est normal !</strong> Chaque étape utilise une IA puissante pour vous donner la meilleure qualité.</p>
            </div>
          )
        },
        {
          q: 'Comment réinitialiser mon mot de passe ?',
          a: (
            <div>
              <p><InlineIcon icon={KeyIcon} color="#D4A574" /> <strong>Réinitialisation simple :</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Sur la page de connexion :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Cliquez sur "Mot de passe oublié ?"</li>
                <li>Entrez votre email</li>
                <li>Vérifiez les logs backend (en développement) ou votre email</li>
                <li>Cliquez sur le lien de réinitialisation</li>
                <li>Créez un nouveau mot de passe (minimum 6 caractères)</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={WarningIcon} color="#FFA726" /> <strong>Compte bloqué ?</strong></p>
              <p>Après 3 tentatives échouées, votre compte est bloqué 30 minutes. Utilisez "Mot de passe oublié" pour débloquer immédiatement !</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'tips',
      icon: <LightbulbIcon size={20} />,
      title: 'Astuces et Conseils',
      questions: [
        {
          q: 'Comment obtenir les meilleures extractions ?',
          a: (
            <div>
              <p><InlineIcon icon={TargetIcon} color="#DC3545" /> <strong>Conseils pour des recettes parfaites :</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Choisissez des vidéos où :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li><InlineIcon icon={CheckIcon} color="#4CAF50" /> Le créateur ÉNONCE clairement chaque ingrédient</li>
                <li><InlineIcon icon={CheckIcon} color="#4CAF50" /> Les quantités sont PRÉCISÉES à voix haute</li>
                <li><InlineIcon icon={CheckIcon} color="#4CAF50" /> Les étapes sont EXPLIQUÉES en détail</li>
                <li><InlineIcon icon={CheckIcon} color="#4CAF50" /> L'audio est CLAIR (pas trop de musique forte)</li>
                <li><InlineIcon icon={CheckIcon} color="#4CAF50" /> La vidéo montre bien le plat final</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><strong>Évitez les vidéos :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li><InlineIcon icon={CloseIcon} color="#DC3545" /> Avec seulement du texte écrit à l'écran</li>
                <li><InlineIcon icon={CloseIcon} color="#DC3545" /> Avec musique très forte qui couvre la voix</li>
                <li><InlineIcon icon={CloseIcon} color="#DC3545" /> Trop rapides sans explications</li>
                <li><InlineIcon icon={CloseIcon} color="#DC3545" /> Qui montrent juste le résultat final</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Créateurs recommandés :</strong> Ceux qui font des recettes ASMR ou éducatives ont généralement des vidéos parfaites pour SmartBouffe !</p>
            </div>
          )
        },
        {
          q: 'Puis-je modifier une recette après extraction ?',
          a: (
            <div>
              <p><InlineIcon icon={EditIcon} /> <strong>Oui, complètement !</strong></p>
              <p style={{ marginTop: '0.5rem' }}><strong>Comment modifier :</strong></p>
              <ol style={{ marginLeft: '1.5rem' }}>
                <li>Ouvrez la recette</li>
                <li>Appuyez longuement sur la recette dans votre liste</li>
                <li>Choisissez "Modifier"</li>
                <li>Changez ce que vous voulez (titre, ingrédients, instructions, etc.)</li>
                <li>Sauvegardez</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><strong>Vous pouvez modifier :</strong></p>
              <ul style={{ marginLeft: '1.5rem' }}>
                <li>Le titre</li>
                <li>Les ingrédients (ajouter, retirer, modifier)</li>
                <li>Les instructions</li>
                <li>Les temps de préparation/cuisson</li>
                <li>Le nombre de portions</li>
              </ul>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={LightbulbIcon} color="#FFA726" /> <strong>Astuce :</strong> Personnalisez vos recettes avec vos propres notes, astuces ou modifications !</p>
            </div>
          )
        },
        {
          q: 'Comment maximiser mes vidéos recettes ?',
          a: (
            <div>
              <p><InlineIcon icon={DiamondIcon} /> <strong>Stratégies d'économie :</strong></p>
              <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li><strong>Privilégiez les vidéos courtes</strong> ({'<'} 3:30) : 1 vidéo recette au lieu de 2</li>
                <li><strong>Planifiez vos extractions</strong> : Attendez d'avoir plusieurs recettes à extraire</li>
                <li><strong>Choisissez le bon forfait</strong> : Si vous extrayez beaucoup, un forfait supérieur sera plus économique</li>
                <li><strong>Évitez les extractions ratées</strong> : Choisissez des vidéos de qualité (voir conseils ci-dessus)</li>
                <li><strong>Vidéos recettes bonus</strong> : Si vous épuisez votre forfait, achetez des bonus plutôt que de passer au forfait supérieur si c'est ponctuel</li>
              </ol>
              <p style={{ marginTop: '1rem' }}><InlineIcon icon={GiftIcon} color="#D4A574" /> <strong>Rappel :</strong> Vos vidéos recettes se renouvellent chaque mois !</p>
            </div>
          )
        }
      ]
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F5F1E8 0%, #E8E4D9 100%)', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '1rem 1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginBottom: '2rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            style={{
              padding: '0.5rem',
              borderRadius: '8px'
            }}
          >
            <ArrowLeft size={24} />
          </Button>
          <SmartbouffeLogoLarge style={{ width: '120px' }} />
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 1.5rem'
      }}>
        {/* Title */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#3A4A3E',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem'
          }}>
            <QuestionIcon size={40} color="#87A96B" />
            Foire aux questions
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#8B7355',
            lineHeight: '1.6'
          }}>
            Tout ce que vous devez savoir pour utiliser SmartBouffe comme un pro !
          </p>
        </div>

        {/* FAQ Sections */}
        {faqSections.map((section) => (
          <div
            key={section.id}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#5D6F61',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: '2px solid #E8E4D9',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              {section.icon}
              {section.title}
            </h2>

            {section.questions.map((item, idx) => (
              <div
                key={`faq-${section.title}-${idx}`}
                style={{
                  borderBottom: idx < section.questions.length - 1 ? '1px solid #F5F1E8' : 'none',
                  paddingBottom: idx < section.questions.length - 1 ? '1rem' : '0',
                  marginBottom: idx < section.questions.length - 1 ? '1rem' : '0'
                }}
              >
                <button
                  onClick={() => toggleSection(`${section.id}-${idx}`)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    padding: '0.75rem',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    textAlign: 'left',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#F5F1E8'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    color: '#3A4A3E',
                    flex: 1
                  }}>
                    {item.q}
                  </span>
                  {openSection === `${section.id}-${idx}` ? (
                    <ChevronUp size={20} color="#87A96B" />
                  ) : (
                    <ChevronDown size={20} color="#87A96B" />
                  )}
                </button>

                {openSection === `${section.id}-${idx}` && (
                  <div style={{
                    padding: '1rem 0.75rem',
                    fontSize: '0.95rem',
                    color: '#5D6F61',
                    lineHeight: '1.8'
                  }}>
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Contact Section */}
        <div style={{
          background: 'linear-gradient(135deg, #87A96B 0%, #6B8A54 100%)',
          borderRadius: '16px',
          padding: '2rem',
          textAlign: 'center',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '0.75rem'
          }}>
            Vous ne trouvez pas votre réponse ?
          </h3>
          <p style={{
            fontSize: '1rem',
            marginBottom: '1rem',
            opacity: 0.9
          }}>
            Contactez notre support pour toute question supplémentaire
          </p>
          <a
            href="mailto:support@smartbouffe.com"
            style={{
              display: 'inline-block',
              background: 'white',
              color: '#87A96B',
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            support@smartbouffe.com
          </a>
        </div>
      </div>

      <AdBanner />
      <Footer />
    </div>
  );
}
